/* eslint-disable no-restricted-imports -- rexports banned imports */
import { createDraftSafeSelectorCreator } from "@reduxjs/toolkit";
import deepEqual from "fast-deep-equal";
import { weakMapMemoize } from "reselect";

import { shallowEqual } from "../../util/index.js";

// we rexport this to allow easy global configuration in the future
export { createDraftSafeSelectorCreator as createSelectorCreator } from "@reduxjs/toolkit";

const sharedSelectorOptions = {
  devModeChecks: {
    // Our usage of `stableEmptyArray` and `stableEmptyObject` causes a fair amount
    // of spurious warnings with this check
    identityFunctionCheck: "never",
  },
} as const;

const selectorOptions = {
  ...sharedSelectorOptions,
  memoize: weakMapMemoize,
  argsMemoize: weakMapMemoize,
} as const;

/**
 * Thin wrapper around {@link https://reselect.js.org/api/createselector/ | `createSelector`}
 * that adds hex specific configuration and  {@link https://redux-toolkit.js.org/api/createSelector#createdraftsafeselector | draft safety}
 */
export const createSelector = createDraftSafeSelectorCreator({
  ...selectorOptions,
});

/**
 * Creates a selector that compares the result of its output selector with
 * `shallowEqual`. Same as passing `memoizeOptions.resultEqualityCheck: shallowEqual`
 * to {@link createSelector | `createSelector`}
 *
 * This will have the same effect as calling the selector with
 * `useSelector(selector, shallowEqual)`, but bakes in the shallowness of the
 * comparison to the selector itself (instead of relying on the consumer). This
 * can also be used when chaining re-select selectors to make an input
 * selector shallow compared.
 */
export const createShallowResultSelector = createDraftSafeSelectorCreator({
  ...selectorOptions,
  memoizeOptions: {
    resultEqualityCheck: shallowEqual,
  },
});

/**
 * Creates a selector that compares the result of its input selectors using
 * `shallowEqual`. Same as passing `argsMemoizeOptions.resultEqualityCheck: shallowEqual`
 * to {@link createSelector | `createSelector`}
 *
 * This will have the same effect as using {@link createShallowResultSelector | `createShallowResultSelector`}
 * to define all of the input selectors. You should generally prefer just using `createShallowResultSelector`
 * on all input selectors
 */
export const createShallowInputsSelector = createDraftSafeSelectorCreator({
  ...selectorOptions,
  argsMemoizeOptions: {
    resultEqualityCheck: shallowEqual,
  },
});

/**
 * Creates a selector that compares the result of both input and output
 * selectors with `shallowEqual`. Same as passing `argsMemoizeOptions.resultEqualityCheck: shallowEqual`
 * and `argsMemoizeOptions.resultEqualityCheck: shallowEqual`
 * to {@link createSelector | `createSelector`}
 *
 * This is the combination of both {@link createShallowInputsSelector | `createShallowInputsSelector`} and
 * {@link createShallowResultSelector | `createShallowResultSelector`}.
 *
 * Generally should prefer having the entire selector chain use {@link createShallowResultSelector | `createShallowResultSelector`} instead
 */
export const createShallowInputsAndResultSelector =
  createDraftSafeSelectorCreator({
    ...selectorOptions,
    memoizeOptions: {
      resultEqualityCheck: shallowEqual,
    },
    argsMemoizeOptions: {
      resultEqualityCheck: shallowEqual,
    },
  });

/**
 * WARNING: Deep equality checks can be expensive. This should only be used with
 * data that is not deeply nested.
 *
 * Creates a selector that compares the result of its output selector with deep
 * equality. Same as passing `memoizeOptions.resultEqualityCheck: deepEqual`
 * to {@link createSelector | `createSelector`}.
 *
 * This will have the same effect as calling the selector with
 * `useSelector(selector, deepEqual)`, but bakes in the deepness of the
 * comparison to the selector itself (instead of relying on the consumer). This
 * can also be used when chaining re-select selectors to make an input selector
 * deep compared.
 */
export const createDeepResultSelector = createDraftSafeSelectorCreator({
  ...selectorOptions,
  memoizeOptions: {
    resultEqualityCheck: deepEqual,
  },
});
